import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Settings, 
  Save, 
  Loader2, 
  Shield, 
  Globe, 
  DollarSign,
  AlertCircle,
  CheckCircle,
  Info
} from "lucide-react";

const SubscriptionSettings = ({ API, token }) => {
  const [settings, setSettings] = useState({
    subscription_management_enabled: false,
    base_currency: "USD",
    auto_currency_conversion: true,
    free_tier_enabled: true,
    trial_days: 0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState(null);

  const currencies = [
    { code: "USD", name: "US Dollar", symbol: "$" },
    { code: "EUR", name: "Euro", symbol: "€" },
    { code: "GBP", name: "British Pound", symbol: "£" },
    { code: "INR", name: "Indian Rupee", symbol: "₹" },
    { code: "JPY", name: "Japanese Yen", symbol: "¥" },
    { code: "AUD", name: "Australian Dollar", symbol: "A$" },
    { code: "CAD", name: "Canadian Dollar", symbol: "C$" },
    { code: "SGD", name: "Singapore Dollar", symbol: "S$" },
    { code: "AED", name: "UAE Dirham", symbol: "د.إ" }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (originalSettings) {
      const changed = JSON.stringify(settings) !== JSON.stringify(originalSettings);
      setHasChanges(changed);
    }
  }, [settings, originalSettings]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/subscription-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
      setOriginalSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to fetch subscription settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/subscription-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOriginalSettings(settings);
      setHasChanges(false);
      toast.success("Subscription settings saved successfully!");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (field) => {
    setSettings(prev => ({ ...prev, [field]: !prev[field] }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Settings Card */}
      <Card className="border-2 border-blue-100">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-blue-600" />
              <CardTitle>Subscription Management Settings</CardTitle>
            </div>
            {hasChanges && (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                Unsaved Changes
              </Badge>
            )}
          </div>
          <CardDescription>
            Configure global subscription management behavior for your platform
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Main Toggle - Subscription Management */}
          <div className="p-4 rounded-lg border-2 border-dashed border-blue-200 bg-blue-50/50">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-blue-600" />
                  <Label className="text-base font-semibold">
                    Enable Subscription Management
                  </Label>
                </div>
                <p className="text-sm text-gray-600">
                  When enabled, users will have access only to modules allowed by their subscription plan.
                  When disabled, all users get full access to all features.
                </p>
              </div>
              <Switch
                checked={settings.subscription_management_enabled}
                onCheckedChange={() => handleToggle("subscription_management_enabled")}
                className="data-[state=checked]:bg-blue-600"
                data-testid="subscription-toggle"
              />
            </div>
            
            {/* Status Indicator */}
            <div className="mt-3 flex items-center gap-2">
              {settings.subscription_management_enabled ? (
                <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Active - Plan-based access enforced
                </Badge>
              ) : (
                <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Inactive - Full access for all users
                </Badge>
              )}
            </div>
          </div>

          {/* Currency Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-gray-500" />
                <Label>Base Currency</Label>
              </div>
              <Select
                value={settings.base_currency}
                onValueChange={(value) => setSettings(prev => ({ ...prev, base_currency: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map(curr => (
                    <SelectItem key={curr.code} value={curr.code}>
                      {curr.symbol} {curr.name} ({curr.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Default currency for subscription plans
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-gray-500" />
                <Label>Auto Currency Conversion</Label>
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <span className="text-sm">
                  Convert prices to user's local currency
                </span>
                <Switch
                  checked={settings.auto_currency_conversion}
                  onCheckedChange={() => handleToggle("auto_currency_conversion")}
                  data-testid="auto-currency-toggle"
                />
              </div>
              <p className="text-xs text-gray-500">
                Users see prices in their country's currency
              </p>
            </div>
          </div>

          {/* Trial & Free Tier Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Free Tier Access</Label>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <span className="text-sm">
                  Allow free tier with limited features
                </span>
                <Switch
                  checked={settings.free_tier_enabled}
                  onCheckedChange={() => handleToggle("free_tier_enabled")}
                  data-testid="free-tier-toggle"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Trial Period (Days)</Label>
              <Input
                type="number"
                min="0"
                max="90"
                value={settings.trial_days}
                onChange={(e) => setSettings(prev => ({ 
                  ...prev, 
                  trial_days: parseInt(e.target.value) || 0 
                }))}
                className="max-w-[200px]"
                data-testid="trial-days-input"
              />
              <p className="text-xs text-gray-500">
                Free trial days for new users (0 = no trial)
              </p>
            </div>
          </div>

          {/* Info Box */}
          <div className="p-4 bg-gray-50 rounded-lg border">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-gray-600">
                <p className="font-medium text-gray-900 mb-1">How it works:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Subscription OFF:</strong> All users get full access to all modules</li>
                  <li><strong>Subscription ON:</strong> Users only access modules mapped in their plan</li>
                  <li><strong>Auto Conversion:</strong> Plan prices shown in user's local currency</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t">
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="min-w-[150px]"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSettings;
